import * as Sentry from '@sentry/react'
import type { ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'

interface FallbackProps {
  error: unknown
  resetError: () => void
}

function getErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message
  }
  return String(error)
}

function getErrorStack(error: unknown): string | undefined {
  if (error instanceof Error) {
    return error.stack
  }
  return undefined
}

function ErrorFallback({ error, resetError }: FallbackProps): ReactElement {
  const { t } = useTranslation('common')

  return (
    <div className="flex min-h-400 flex-col items-center justify-center p-8 text-center">
      <div className="mb-4 text-6xl">⚠️</div>
      <h2 className="mb-2 text-2xl font-bold">{t('errors.generic')}</h2>
      <p className="mb-6 max-w-md text-muted-foreground">
        {import.meta.env.DEV ? getErrorMessage(error) : t('errors.serverError')}
      </p>
      <div className="flex gap-4">
        <Button onClick={resetError}>{t('tryAgain')}</Button>
        <Button variant="outline" onClick={() => window.location.reload()}>
          {t('back')}
        </Button>
      </div>
      {import.meta.env.DEV && getErrorStack(error) && (
        <pre className="mt-6 max-w-full overflow-auto rounded-md bg-muted p-4 text-left text-sm">
          <code>{getErrorStack(error)}</code>
        </pre>
      )}
    </div>
  )
}

interface ErrorBoundaryProps {
  children: React.ReactNode
  fallback?: ReactElement
}

export function ErrorBoundary({ children, fallback }: ErrorBoundaryProps) {
  return (
    <Sentry.ErrorBoundary
      fallback={({ error, resetError }): ReactElement =>
        fallback ?? <ErrorFallback error={error} resetError={resetError} />
      }
      showDialog={import.meta.env.PROD}
    >
      {children}
    </Sentry.ErrorBoundary>
  )
}
